import {
  ActivityIndicator,
  Badge,
  Button,
  Chip,
  Icon,
  mergeTailwindClasses,
  SocialMediaIcon,
  SocialMediaIconProps,
} from '@kontentino/ui';
import React, { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import PageTypeUtils from 'app/utils/pageType';

type Props = {
  type: number;
  onClick: () => void;
  dataName?: string;
  connectedCount?: number;
  ctaLabel?: string;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
};

const SOCIAL_MEDIA_TYPES = [
  { type: TYPE.FACEBOOK, descriptionKey: 'newSocialMediaProfile.businessPage' },
  {
    type: TYPE.INSTAGRAM,
    descriptionKey: 'newSocialMediaProfile.businessOrCreator',
  },
  {
    type: TYPE.TIKTOK,
    descriptionKey: 'newSocialMediaProfile.businessOrCreator',
  },
  {
    type: TYPE.LINKEDIN,
    descriptionKey: 'newSocialMediaProfile.pageOrProfile',
  },
  { type: TYPE.TWITTER, descriptionKey: 'newSocialMediaProfile.profileType' },
  {
    type: TYPE.PINTEREST,
    descriptionKey: 'newSocialMediaProfile.businessOrProfile',
  },
  {
    type: TYPE.GOOGLE_MY_BUSINESS,
    descriptionKey: 'newSocialMediaProfile.pageType',
  },
  { type: TYPE.THREADS, descriptionKey: 'newSocialMediaProfile.profileType' },
  {
    type: TYPE.YOUTUBE,
    descriptionKey: 'newSocialMediaProfile.businessOrCreator',
  },
];

const SOCIAL_MEDIA_TYPE_OPTION_CONFIG = SOCIAL_MEDIA_TYPES.reduce(
  (config, { type, descriptionKey, ...rest }) => {
    config[type] = {
      key: PageTypeUtils.getName(type),
      title: PageTypeUtils.getLabel(type),
      descriptionKey,
      ...rest,
    };
    return config;
  },
  {} as {
    [key: number]: {
      key: string;
      title: string;
      descriptionKey: string;
      comingSoon?: boolean;
      new?: boolean;
      beta?: boolean;
    };
  },
);

const SelectSocialTypeOption: FC<Props> = ({
  type,
  onClick,
  dataName,
  connectedCount,
  ctaLabel,
  className,
  isLoading,
  disabled,
}) => {
  const { t } = useTranslation();
  const socialMedia = SOCIAL_MEDIA_TYPE_OPTION_CONFIG[type];
  const [isRedirectingInternally, setIsRedirectingInternally] = useState(false);
  const isControlled = isLoading !== undefined;
  const isRedirecting = isControlled ? isLoading : isRedirectingInternally;
  const isDisabled = !!disabled || isRedirecting;

  const handleClick = () => {
    if (socialMedia.comingSoon || isDisabled) {
      return;
    }
    if (!isControlled) {
      setIsRedirectingInternally(true);
    }
    onClick();
  };

  return (
    <div
      tabIndex={0}
      className={mergeTailwindClasses(
        clsx(
          'tw-group tw-relative tw-flex tw-h-[120px] tw-w-full tw-select-none tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-md tw-border tw-border-grayscale-10 tw-bg-transparent tw-text-center tw-outline-none tw-transition-all tw-duration-300',
          className,
          {
            'tw-cursor-pointer hover:tw-border-primary-100 focus-visible:tw-border-primary-100':
              !socialMedia.comingSoon && !isDisabled,
            'tw-pointer-events-none tw-border-primary-100': isRedirecting,
            'tw-pointer-events-none tw-opacity-50':
              isDisabled && !isRedirecting,
          },
        ),
      )}
      role="button"
      aria-disabled={isDisabled}
      aria-busy={isRedirecting}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      data-cy={`connect-social-media-profile_${socialMedia.key}`}
      data-name={dataName}
    >
      <SocialMediaIcon
        type={socialMedia.key as SocialMediaIconProps['type']}
        variant="plain"
        size={28}
      />
      <div
        className={clsx('tw-h-[28px]', {
          'group-hover:tw-hidden group-focus-visible:tw-hidden':
            !socialMedia.comingSoon && !isDisabled,
          'tw-hidden': isRedirecting,
        })}
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-text-md tw-font-medium tw-text-grayscale-180">
          {socialMedia?.title}
        </div>
        <div className="tw-text-xs tw-text-grayscale-140">
          {t(socialMedia?.descriptionKey)}
        </div>
      </div>
      {isRedirecting && <ActivityIndicator className="tw-h-4 tw-w-4" />}
      {!socialMedia.comingSoon && !isDisabled && (
        <Button
          className="tw-hidden group-hover:tw-block group-focus-visible:tw-block"
          variant="tertiary"
          size="small"
          tabIndex={-1}
        >
          {ctaLabel ?? t('continue')}
        </Button>
      )}
      {isRedirectingInternally &&
        createPortal(
          <div
            className="tw-fixed tw-inset-0 tw-z-[9999] tw-cursor-wait"
            aria-hidden="true"
          />,
          document.body,
        )}
      {!!connectedCount && (
        <Chip
          variant="primary"
          className="tw-absolute tw-right-2 tw-top-2 tw-h-4 tw-min-w-[16px] tw-rounded-full tw-text-xs tw-font-medium"
          data-cy={`connect-social-media-profile_${socialMedia.key}_connected-count`}
        >
          {connectedCount}
        </Chip>
      )}
      {socialMedia.comingSoon && (
        <Badge variant="purple" className="tw-absolute tw-right-1 tw-top-1">
          <Icon size="sm" icon={faSparkles} />
          {t('comingSoon')}
        </Badge>
      )}
      {socialMedia.new && (
        <Badge
          variant="gray"
          className="tw-absolute tw-right-1 tw-top-1 tw-capitalize"
        >
          <Icon size="sm" icon={faSparkles} />
          {t('new')}
        </Badge>
      )}
      {socialMedia.beta && (
        <Badge
          variant="gray"
          className="tw-absolute tw-right-1 tw-top-1 tw-capitalize"
        >
          <Icon size="sm" icon={faSparkles} />
          {t('beta')}
        </Badge>
      )}
    </div>
  );
};

export default SelectSocialTypeOption;
